import { parseEventLogs } from "viem";
import { protocolErrorAbi } from "./errors.js";
import { projectCoreAbi } from "./project.js";
import { LiquidityMode, ProjectState, nativeAmount, tokenAmount, } from "./types.js";
const uintOutput = [{ name: "", type: "uint256" }];
export const PROTOCOL_LP_TOKEN_AMOUNT = tokenAmount(50000000n * 10n ** 18n);
export const PROTOCOL_LP_RECIPIENT = "0x000000000000000000000000000000000000dEaD";
export const liquidityProjectCoreAbi = [
    ...projectCoreAbi,
    {
        type: "function",
        name: "liquidityActivationFailAfter",
        stateMutability: "view",
        inputs: [],
        outputs: [{ name: "", type: "uint64" }],
    },
    {
        type: "function",
        name: "failStalledLiquidityActivation",
        stateMutability: "nonpayable",
        inputs: [],
        outputs: [],
    },
    ...["liquidityNetRaised", "liquidityTokenAmount", "liquidityWbnbAmount", "liquidityMinted"].map((name) => ({
        type: "function",
        name,
        stateMutability: "view",
        inputs: [],
        outputs: uintOutput,
    })),
    {
        type: "function",
        name: "lpRecipient",
        stateMutability: "view",
        inputs: [],
        outputs: [{ name: "", type: "address" }],
    },
    {
        type: "function",
        name: "activateProtocolLiquidity",
        stateMutability: "nonpayable",
        inputs: [
            { name: "minLpOut", type: "uint256" },
            { name: "deadline", type: "uint256" },
        ],
        outputs: [
            { name: "pairAddress", type: "address" },
            { name: "liquidity", type: "uint256" },
        ],
    },
    {
        type: "function",
        name: "withdrawCreatorProceeds",
        stateMutability: "nonpayable",
        inputs: [{ name: "receiver", type: "address" }],
        outputs: [{ name: "amount", type: "uint256" }],
    },
    {
        type: "function",
        name: "confirmCreatorLiquidity",
        stateMutability: "nonpayable",
        inputs: [{ name: "pairAddress", type: "address" }],
        outputs: [],
    },
];
export const pancakeV2FactoryAbi = [
    ...protocolErrorAbi,
    {
        type: "function",
        name: "getPair",
        stateMutability: "view",
        inputs: [
            { name: "tokenA", type: "address" },
            { name: "tokenB", type: "address" },
        ],
        outputs: [{ name: "pair", type: "address" }],
    },
];
export const pancakeV2PairAbi = [
    ...protocolErrorAbi,
    ...["factory", "token0", "token1"].map((name) => ({
        type: "function",
        name,
        stateMutability: "view",
        inputs: [],
        outputs: [{ name: "", type: "address" }],
    })),
    {
        type: "function",
        name: "getReserves",
        stateMutability: "view",
        inputs: [],
        outputs: [
            { name: "reserve0", type: "uint112" },
            { name: "reserve1", type: "uint112" },
            { name: "blockTimestampLast", type: "uint32" },
        ],
    },
    ...["totalSupply", "MINIMUM_LIQUIDITY"].map((name) => ({
        type: "function",
        name,
        stateMutability: "view",
        inputs: [],
        outputs: uintOutput,
    })),
];
export const liquidityEventAbi = [
    {
        type: "event",
        name: "LiquidityActivationTimedOut",
        inputs: [
            { name: "project", type: "address", indexed: true },
            { name: "caller", type: "address", indexed: true },
            { name: "graduatedAt", type: "uint64", indexed: false },
            { name: "failedAt", type: "uint64", indexed: false },
            { name: "refundableNative", type: "uint256", indexed: false },
            { name: "refundablePayment", type: "uint256", indexed: false },
            { name: "projectTokensBurned", type: "uint256", indexed: false },
        ],
    },
    {
        type: "event",
        name: "PaymentLiquidityActivated",
        inputs: [
            { name: "project", type: "address", indexed: true },
            { name: "pair", type: "address", indexed: true },
            { name: "paymentAsset", type: "address", indexed: true },
            { name: "tokenAmount", type: "uint256", indexed: false },
            { name: "paymentAmount", type: "uint256", indexed: false },
            { name: "liquidityBurned", type: "uint256", indexed: false },
            { name: "burnAddress", type: "address", indexed: false },
            { name: "activatedAt", type: "uint64", indexed: false },
        ],
    },
    {
        type: "event",
        name: "PaymentCreatorProceedsWithdrawn",
        inputs: [
            { name: "project", type: "address", indexed: true },
            { name: "creator", type: "address", indexed: true },
            { name: "paymentAsset", type: "address", indexed: true },
            { name: "receiver", type: "address", indexed: false },
            { name: "amount", type: "uint256", indexed: false },
        ],
    },
    {
        type: "event",
        name: "ProtocolLiquidityActivated",
        inputs: [
            { name: "project", type: "address", indexed: true },
            { name: "pair", type: "address", indexed: true },
            { name: "caller", type: "address", indexed: true },
            { name: "tokenAmount", type: "uint256", indexed: false },
            { name: "wbnbAmount", type: "uint256", indexed: false },
            { name: "liquidityBurned", type: "uint256", indexed: false },
            { name: "burnAddress", type: "address", indexed: false },
            { name: "activatedAt", type: "uint64", indexed: false },
        ],
    },
    {
        type: "event",
        name: "CreatorProceedsWithdrawn",
        inputs: [
            { name: "project", type: "address", indexed: true },
            { name: "creator", type: "address", indexed: true },
            { name: "receiver", type: "address", indexed: true },
            { name: "amountWei", type: "uint256", indexed: false },
        ],
    },
    {
        type: "event",
        name: "CreatorLiquidityConfirmed",
        inputs: [
            { name: "project", type: "address", indexed: true },
            { name: "pair", type: "address", indexed: true },
            { name: "creator", type: "address", indexed: true },
            { name: "reserveToken", type: "uint112", indexed: false },
            { name: "reserveWbnb", type: "uint112", indexed: false },
            { name: "pairTotalSupply", type: "uint256", indexed: false },
            { name: "confirmedAt", type: "uint64", indexed: false },
        ],
    },
];
export function previewProtocolLiquidity(totalRaised) {
    const treasuryFee = nativeAmount(totalRaised / 100n);
    return {
        tokenAmount: PROTOCOL_LP_TOKEN_AMOUNT,
        wbnbAmount: nativeAmount(totalRaised - treasuryFee),
        treasuryFee,
        lpRecipient: PROTOCOL_LP_RECIPIENT,
    };
}
export function liquidityCapabilities(snapshot) {
    const isPending = snapshot.state === ProjectState.GRADUATED_PENDING_LP;
    const isCreator = snapshot.connectedAccount !== undefined &&
        snapshot.connectedAccount.toLowerCase() === snapshot.creator.toLowerCase();
    return {
        canActivateProtocol: isPending && !snapshot.paused && snapshot.mode === LiquidityMode.PROTOCOL_LOCKED_V2,
        canWithdrawCreatorProceeds: isCreator &&
            snapshot.mode === LiquidityMode.CREATOR_MANAGED_V2 &&
            snapshot.creatorProceeds > 0n,
        canConfirmCreatorPair: isPending &&
            !snapshot.paused &&
            isCreator &&
            snapshot.mode === LiquidityMode.CREATOR_MANAGED_V2,
        claimsLocked: isPending,
        creatorManagedRiskPermanent: snapshot.mode === LiquidityMode.CREATOR_MANAGED_V2,
    };
}
export function protocolActivationAction(minLpOut, deadline) {
    if (minLpOut < 0n)
        throw new RangeError("Minimum LP output must not be negative");
    if (deadline < 0n)
        throw new RangeError("Deadline must not be negative");
    return { functionName: "activateProtocolLiquidity", args: [minLpOut, deadline] };
}
export function creatorProceedsAction(receiver) {
    return { functionName: "withdrawCreatorProceeds", args: [receiver] };
}
export function creatorLiquidityAction(pair) {
    return { functionName: "confirmCreatorLiquidity", args: [pair] };
}
export function parseLiquidityReceipt(receipt) {
    return parseEventLogs({ abi: liquidityEventAbi, logs: receipt.logs, strict: false });
}
//# sourceMappingURL=liquidity.js.map